import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Upload, Download, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

interface BulkSEOImportProps {
  userId: string;
  onImportComplete: () => void;
}

interface ImportLog {
  id: string;
  filename: string;
  total_rows: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
  status: string;
  error_log: any;
}

export const BulkSEOImport = ({ userId, onImportComplete }: BulkSEOImportProps) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [currentImport, setCurrentImport] = useState<ImportLog | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processCSV(e.dataTransfer.files[0]);
    }
  }, [userId]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processCSV(e.target.files[0]);
    }
  };

  const processCSV = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File',
        description: 'Please upload a CSV file',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const requiredColumns = ['url', 'keyword', 'target_page'];
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));
      
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      // Create import log
      const { data: logData, error: logError } = await supabase
        .from('seo_import_logs')
        .insert({
          agent_id: userId,
          filename: file.name,
          total_rows: data.length,
          status: 'processing'
        })
        .select()
        .single();

      if (logError) throw logError;

      setCurrentImport(logData);

      // Process CSV data via edge function
      const { data: result, error: processError } = await supabase.functions.invoke('process-seo-csv', {
        body: {
          data,
          importLogId: logData.id,
          userId
        }
      });

      if (processError) throw processError;

      toast({
        title: 'Import Complete',
        description: `Successfully processed ${result.created} keywords, updated ${result.updated}, failed ${result.failed}`,
      });

      setCurrentImport(result.importLog);
      onImportComplete();
    } catch (error: any) {
      console.error('CSV processing error:', error);
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to process CSV file',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'url,keyword,target_page,meta_title,meta_description,category\nhttps://www.aureum7trading.com/products/aria,luxury smart toilet,https://www.aureum7trading.com/products/aria,ARIA H60 - Premium Smart Toilet | Aureum VII Trading,Discover the ARIA H60 luxury smart toilet with advanced hygiene features and modern design.,smart-toilets\nhttps://www.aureum7trading.com/products/vena,bidet toilet seat,https://www.aureum7trading.com/products/vena,VENA M80F - Smart Bidet Toilet | Aureum VII,Experience ultimate comfort with VENA M80F smart bidet toilet seat.,smart-toilets';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'seo_keywords_template.csv';
    a.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Bulk SEO Keyword Import
        </CardTitle>
        <CardDescription>
          Upload CSV with keywords, URLs, and metadata for automatic SEO generation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={downloadTemplate} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive ? 'border-primary bg-primary/5' : 'border-muted'
          } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Processing CSV...</p>
            </div>
          ) : (
            <>
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="mb-2 text-sm font-medium">
                Drag and drop your CSV file here
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                or click to browse
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                className="hidden"
                id="csv-upload"
              />
              <Button asChild variant="outline">
                <label htmlFor="csv-upload" className="cursor-pointer">
                  Select CSV File
                </label>
              </Button>
            </>
          )}
        </div>

        {currentImport && (
          <Alert>
            {currentImport.status === 'completed' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">{currentImport.filename}</p>
                <div className="text-sm space-y-1">
                  <div>Total rows: {currentImport.total_rows}</div>
                  <div className="text-green-600">Created: {currentImport.created_count}</div>
                  <div className="text-blue-600">Updated: {currentImport.updated_count}</div>
                  {currentImport.failed_count > 0 && (
                    <div className="text-red-600">Failed: {currentImport.failed_count}</div>
                  )}
                  <div className="capitalize">Status: {currentImport.status}</div>
                </div>
                {currentImport.error_log && (
                  <details className="text-xs mt-2">
                    <summary className="cursor-pointer">View errors</summary>
                    <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-40">
                      {JSON.stringify(currentImport.error_log, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertDescription className="text-xs">
            <strong>CSV Format:</strong> url, keyword, target_page, meta_title (optional), meta_description (optional), category (optional)
            <br />
            <strong>System automatically:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Generates SEO metadata (title, description, alt text)</li>
              <li>Verifies URLs are accessible</li>
              <li>Submits to Google & Bing indexing APIs</li>
              <li>Updates keyword rankings and status</li>
              <li>Tracks progress in real-time</li>
            </ul>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};